import React from "react";
import clsx from "clsx";
import styles from "./MemberAvatar.module.scss";

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

interface MemberAvatarProps {
	/** Identificador estável do membro — usado para gerar a cor de fundo consistente. */
	id: string;
	/** Nome para extrair iniciais. */
	name: string;
	/** Tamanho visual. */
	size?: AvatarSize;
	/** Status do trabalho atual — adiciona indicador colorido (ring + dot) */
	status?: "pending" | "processing" | "paused" | "completed" | "failed" | "cancelled" | null;
	/** URL opcional de imagem. Se válida, sobrescreve o gradient + iniciais. */
	imageUrl?: string | null;
	className?: string;
}

/**
 * Paleta de cores consistente — escolhida deterministicamente a partir do hash
 * do `id`. Cada membro tem sempre a mesma cor, ajudando o usuário a reconhecê-lo
 * visualmente em qualquer ponto da aplicação.
 */
const PALETTE: Array<[string, string]> = [
	["#6366f1", "#4338ca"], // indigo
	["#ec4899", "#be185d"], // pink
	["#22c55e", "#15803d"], // green
	["#f59e0b", "#b45309"], // amber
	["#06b6d4", "#0e7490"], // cyan
	["#8b5cf6", "#6d28d9"], // violet
	["#ef4444", "#b91c1c"], // red
	["#0ea5e9", "#0369a1"], // sky
	["#14b8a6", "#0f766e"], // teal
	["#f97316", "#c2410c"], // orange
	["#84cc16", "#4d7c0f"], // lime
	["#a855f7", "#7e22ce"], // purple
];

const hashCode = (input: string): number => {
	let h = 0;
	for (let i = 0; i < input.length; i++) {
		h = (h << 5) - h + input.charCodeAt(i);
		h |= 0;
	}
	return Math.abs(h);
};

const initialsOf = (name: string): string => {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const MemberAvatar: React.FC<MemberAvatarProps> = ({
	id,
	name,
	size = "md",
	status,
	imageUrl,
	className,
}) => {
	const [from, to] = PALETTE[hashCode(id || name) % PALETTE.length];
	const initials = initialsOf(name);
	const bg = `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;

	return (
		<div
			className={clsx(
				styles.wrapper,
				styles[`size_${size}` as const],
				status && styles[`status_${status}` as const],
				className,
			)}
			title={name}
		>
			<div
				className={styles.avatar}
				style={imageUrl ? undefined : { background: bg }}
			>
				{imageUrl ? (
					<img src={imageUrl} alt={name} className={styles.image} />
				) : (
					<span className={styles.initials}>{initials}</span>
				)}
			</div>
			{status && status !== "pending" && (
				<span className={clsx(styles.statusDot, styles[`dot_${status}` as const])} />
			)}
		</div>
	);
};
